import { z, Schema } from 'zod';
import {
  Channel,
  ChannelSchema,
  DefinitionStatusType,
  DefinitionStatusSchema,
  FieldDefinition,
  FieldDefinitionSchema
} from './schema';
import { ZoneDefinition, ZoneDefinitionSchema } from './zone-definition';

/**
 * Structure of a module definition.
 * @typedef ModuleDefinition
 * @property - label - A human readable name of the web module created from this module definition.
 * @property - description - A human readable description.
 * @property - status - 'active' / 'inactive' state of the web module.
 * @property - channels - An array indicating where module instances of this type are allowed to appear.
 * @property - fieldDefinitions - An array of field definitions that define the type of data that can be placed on a module configuration.
 * @property - zoneDefinitions - An optional array of zone definitions that can be used to subdivide the module for the purposes of placing children modules within the module configuration.
 * @property - validations - An optional object for defining rules about how a module configuration can be instantiated.
 */
export type ModuleDefinition = {
  readonly label: string;
  readonly description: string;
  readonly status: DefinitionStatusType;
  readonly channels: ReadonlyArray<Channel>;
  readonly fieldDefinitions: ReadonlyArray<FieldDefinition>;
  readonly zonesDefinitions?: ReadonlyArray<ZoneDefinition>;
  readonly validations?: {
    surfaceRestrictions?: ReadonlyArray<{
      readonly name?: string;
      readonly version?: string;
    }>;
  };
};

export const ModuleDefinitionSchema: Schema<ModuleDefinition> = z.object({
  label: z.string(),
  description: z.string(),
  status: DefinitionStatusSchema,
  channels: z.array(ChannelSchema),
  fieldDefinitions: z.array(FieldDefinitionSchema),
  zonesDefinitions: z.array(ZoneDefinitionSchema).optional(),
  validations: z
    .object({
      surfaceRestrictions: z
        .array(
          z.object({
            name: z.string().optional(),
            version: z.string().optional()
          })
        )
        .optional()
    })
    .optional()
});

/**
 * Indexable version type of a module defintion.
 * @typedef ModuleDefinitionVersion
 * @property - version - Format in whole number.
 */
export type ModuleDefinitionVersion = {
  readonly [version: string]: ModuleDefinition;
};

export const ModuleDefinitionVersionSchema: Schema<ModuleDefinitionVersion> =
  z.record(z.string(), ModuleDefinitionSchema);

/**
 * Structure of a web module definition.
 * @typedef WebModuleDefinition
 * @property - name - The type of module instance that will be created from this definition.
 * @property - schema - A semantic version.
 * @property - type - Defines a web module definition type.
 * @property - versions - Supports module definition versioning.
 */
export type WebModuleDefinition = {
  readonly name: string;
  readonly schema: string;
  readonly type: 'module';
  readonly versions: ModuleDefinitionVersion;
};

export const WebModuleDefinitionSchema: Schema<WebModuleDefinition> = z.object({
  name: z.string(),
  schema: z.string(),
  type: z.literal('module'),
  versions: ModuleDefinitionVersionSchema
});

/**
 * Create a versioned module from a module definition json.
 * @param  definition  Module definition json, conforming with ModuleDefinition type.
 * @returns - Module definition object.
 * @example
 * ```ts
 *    import { createModuleDefinitionVersion } from "@atlassiansox/bxp-definition-sdk";
 *
 *    export const ExampleWebModule = createModuleDefinitionVersion({
 *      {
 *         label: 'signup-wac',
 *         description: 'Test module description',
 *         status: 'active',
 *         channels: ['web', 'mobile'],
 *         fieldDefinitions: [
 *             {
 *              type: "string",
 *              name: "title",
 *              validations: {
 *                isRequired: true,
 *              }
 *            },
 *            {
 *              type: "boolean",
 *              name: "isProduction",
 *              validations: {
 *                isRequired: true,
 *              }
 *            }
 *         ]
 *       }
 *    });
 * ```
 */

export function createModuleDefinitionVersion<const M extends ModuleDefinition>(
  moduleDefinition: M
): M {
  return ModuleDefinitionSchema.parse(moduleDefinition) as M;
}
